import { defineStore } from 'pinia'

const API_BASE_URL = import.meta.env.DEV 
  ? '/api'  // Use Vite proxy in dev (proxies to https://admin.dgsolutions.se/api)
  : '/api'  // Keep as-is for prod (adjust if needed)

export const useServiceAuthStore = defineStore('serviceAuth', {
  state: () => ({
    token: localStorage.getItem('ai_service_token'),
    expiresAt: localStorage.getItem('ai_service_expires_at'),
    scopes: JSON.parse(localStorage.getItem('ai_service_scopes') || '[]'),
    loading: false,
    error: null,
    refreshPromise: null, // Prevent multiple simultaneous refresh attempts
    clientId: import.meta.env.VITE_AI_CLIENT_ID,
    clientSecret: import.meta.env.VITE_AI_CLIENT_SECRET,
  }),
  
  getters: {
    isAuthenticated: (state) => {
      return state.token && state.expiresAt && new Date() < new Date(state.expiresAt)
    },
    
    hasScope: (state) => {
      return (requiredScope) => state.scopes.includes(requiredScope)
    },
    
    timeUntilExpiry: (state) => {
      if (!state.expiresAt) return 0
      const now = new Date()
      const expiry = new Date(state.expiresAt)
      return Math.max(0, expiry - now)
    },
    
    hasCredentials: (state) => {
      return !!(state.clientId && state.clientSecret)
    }
  },
  
  actions: {
    setToken(tokenData) {
      this.token = tokenData.access
      this.expiresAt = tokenData.expires_at
      this.scopes = tokenData.scopes || []
      
      // Persist in localStorage
      localStorage.setItem('ai_service_token', this.token)
      localStorage.setItem('ai_service_expires_at', this.expiresAt)
      localStorage.setItem('ai_service_scopes', JSON.stringify(this.scopes))
      
      // Schedule auto-refresh
      this.scheduleTokenRefresh()
    },
    
    clearToken() {
      this.token = null
      this.expiresAt = null
      this.scopes = []
      this.error = null
      this.refreshPromise = null
      
      // Clear localStorage
      localStorage.removeItem('ai_service_token')
      localStorage.removeItem('ai_service_expires_at')
      localStorage.removeItem('ai_service_scopes')
      
      // Clear any scheduled refresh
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout)
        this.refreshTimeout = null
      }
      
      },
    
    async authenticate() {
      if (!this.hasCredentials) {
        const error = 'AI service credentials not configured. Please set VITE_AI_CLIENT_ID and VITE_AI_CLIENT_SECRET environment variables.'
        this.error = error
        throw new Error(error)
      }
      
      // Debug: Log what credentials we're using (REMOVE IN PRODUCTION!)
      console.log('🔑 Service Auth Debug:', {
        hasClientId: !!this.clientId,
        hasClientSecret: !!this.clientSecret,
        clientIdLength: this.clientId?.length || 0,
        clientSecretLength: this.clientSecret?.length || 0,
        clientIdPrefix: this.clientId?.substring(0, 15) + '...',
        clientSecretPrefix: this.clientSecret?.substring(0, 10) + '...'
      })
      
      this.loading = true
      this.error = null
      
      try {
        
        const response = await fetch(`${API_BASE_URL}/service-token/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            client_id: this.clientId,
            client_secret: this.clientSecret
          })
        })
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || `Authentication failed: ${response.status}`)
        }
        
        const data = await response.json()
        this.setToken(data)
        
        
        return data
      } catch (err) {
        this.error = err.message
        console.error('Service authentication failed:', err)
        throw err
      } finally {
        this.loading = false
      }
    },
    
    async refreshToken() {
      // If a refresh is already in progress, wait for it
      if (this.refreshPromise) {
        return this.refreshPromise
      }
      
      // If token is still valid for more than 2 minutes, no need to refresh
      if (this.timeUntilExpiry > 2 * 60 * 1000) {
        return
      }
      
      this.refreshPromise = this.authenticate()
      
      try {
        await this.refreshPromise
        this.refreshPromise = null
      } catch (error) {
        this.refreshPromise = null
        throw error
      }
    },
    
    async initialize() {
      if (!this.isAuthenticated) {
        await this.authenticate()
      }
    },
    
    async apiRequest(endpoint, options = {}) {
      // Ensure we have a valid token
      if (!this.isAuthenticated) {
        await this.authenticate()
      } else {
        await this.refreshToken()
      }
      
      const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
      const config = {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      }
      
      const response = await fetch(url, config)
      
      // Handle token expiry
      if (response.status === 401) {
        await this.authenticate()
        
        // Retry the request with new token
        config.headers['Authorization'] = `Bearer ${this.token}`
        return fetch(url, config)
      }
      
      return response
    },
    
    scheduleTokenRefresh() {
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout)
      }
      
      // Refresh 5 minutes before expiry
      const refreshTime = this.timeUntilExpiry - (5 * 60 * 1000)
      if (refreshTime > 0) {
        this.refreshTimeout = setTimeout(async () => {
          try {
            await this.authenticate()
          } catch (error) {
            console.error('Automatic AI service token refresh failed:', error)
          }
        }, refreshTime)
      }
    }
  }
})